export type ThemeColorMode = "light" | "dark"

export const DEFAULT_THEME_COLOR = "#0052D9"
export const DEEP_BLUE_THEME_COLOR = "#00BB99"
const BLACK_GOLD_THEME_COLOR = "#F4D8A7"

export interface ThemeColorScheme {
  seedColor: string
  mode: ThemeColorMode
  primary: string
  hover: string
  active: string
  onPrimary: string
  primaryRgb: string
  primarySubtle: string
  primary50: string
  primary100: string
  primary200: string
  primary300: string
  primary400: string
  primaryLight3: string
  primaryLight5: string
  primaryLight7: string
  primaryLight8: string
  primaryLight9: string
  primaryDark2: string
  textPrimary: string
  textRegular: string
  textSecondary: string
  textPlaceholder: string
  textDisabled: string
  textInk: string
  pageBg: string
  surfaceBg: string
  elevatedBg: string
  fillSubtle: string
  fillMuted: string
  fillDisabled: string
  borderSubtle: string
  border: string
  borderStrong: string
  chartAxis: string
  chartGrid: string
  chartTooltipBg: string
  chartTooltipBorder: string
  tableHeaderBg: string
  tableRowHoverBg: string
  tagMutedBg: string
  tagNeutralBg: string
  sidebarBg: string
  sidebarText: string
  sidebarActiveBg: string
  businessContainerBg: string
  categoryNameColor: string
  breadcrumbColor: string
  categoryListBg: string
  categoryListColor: string
  categoryListActiveBg: string
  categoryListActiveColor: string
  categoryListActiveHoverBg: string
  businessButtonBg: string
  businessButtonText: string
  businessButtonBorder: string
  businessButtonHoverBg: string
  businessButtonHoverShadow: string
  businessButtonActiveText: string
  businessButtonActiveBg: string
  tableEmptyText: string
  loadingMaskBg: string
  pageLoadingMaskBg: string
  pageLoadingSpinnerColor: string
  chartColors: string[]
}

interface RgbColor {
  r: number
  g: number
  b: number
}

interface HslColor {
  h: number
  s: number
  l: number
}

type ThemeSemanticColors = Omit<
  ThemeColorScheme,
  | "seedColor"
  | "mode"
  | "primary"
  | "hover"
  | "active"
  | "onPrimary"
  | "primaryRgb"
  | "primarySubtle"
  | "primary50"
  | "primary100"
  | "primary200"
  | "primary300"
  | "primary400"
  | "primaryLight3"
  | "primaryLight5"
  | "primaryLight7"
  | "primaryLight8"
  | "primaryLight9"
  | "primaryDark2"
  | "chartColors"
>

const WHITE = "#FFFFFF"
const BLACK = "#000000"
const DARK_MIX_BG = "#141414"
const LIGHT_SURFACE = "#FFFFFF"
const DARK_SURFACE = "#22201C"
const CHART_HUE_OFFSETS = [92, 36, 210, 158, 285]

const clamp = (value: number, min = 0, max = 1) => {
  return Math.min(max, Math.max(min, value))
}

const toHexPart = (value: number) => {
  return Math.round(clamp(value, 0, 255)).toString(16).padStart(2, "0")
}

const rgbToHex = ({ r, g, b }: RgbColor) => {
  return `#${toHexPart(r)}${toHexPart(g)}${toHexPart(b)}`.toUpperCase()
}

export const normalizeHexColor = (value: string | null | undefined) => {
  if (!value) return null
  const raw = value.trim()
  const shortMatch = raw.match(/^#?([0-9a-f]{3})$/i)
  if (shortMatch) {
    const [, hex] = shortMatch
    return `#${hex.split("").map(char => `${char}${char}`).join("")}`.toUpperCase()
  }

  const match = raw.match(/^#?([0-9a-f]{6})$/i)
  return match ? `#${match[1].toUpperCase()}` : null
}

const hexToRgb = (value: string): RgbColor => {
  const normalized = normalizeHexColor(value) || DEFAULT_THEME_COLOR
  const hex = normalized.slice(1)
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  }
}

const mixColor = (color: string, target: string, amount: number) => {
  const sourceRgb = hexToRgb(color)
  const targetRgb = hexToRgb(target)
  const ratio = clamp(amount)
  return rgbToHex({
    r: sourceRgb.r * (1 - ratio) + targetRgb.r * ratio,
    g: sourceRgb.g * (1 - ratio) + targetRgb.g * ratio,
    b: sourceRgb.b * (1 - ratio) + targetRgb.b * ratio,
  })
}

const rgbToHsl = ({ r, g, b }: RgbColor): HslColor => {
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2

  if (max === min) {
    return { h: 0, s: 0, l: lightness }
  }

  const delta = max - min
  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min)

  let hue = 0
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0)
  } else if (max === green) {
    hue = (blue - red) / delta + 2
  } else {
    hue = (red - green) / delta + 4
  }

  return { h: hue * 60, s: saturation, l: lightness }
}

const hueToRgb = (p: number, q: number, t: number) => {
  let value = t
  if (value < 0) value += 1
  if (value > 1) value -= 1
  if (value < 1 / 6) return p + (q - p) * 6 * value
  if (value < 1 / 2) return q
  if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6
  return p
}

const hslToHex = ({ h, s, l }: HslColor) => {
  const hue = ((h % 360) + 360) % 360 / 360
  const saturation = clamp(s)
  const lightness = clamp(l)

  if (saturation === 0) {
    const grey = lightness * 255
    return rgbToHex({ r: grey, g: grey, b: grey })
  }

  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation
  const p = 2 * lightness - q

  return rgbToHex({
    r: hueToRgb(p, q, hue + 1 / 3) * 255,
    g: hueToRgb(p, q, hue) * 255,
    b: hueToRgb(p, q, hue - 1 / 3) * 255,
  })
}

const relativeLuminance = (color: string) => {
  const { r, g, b } = hexToRgb(color)
  const channel = (value: number) => {
    const ratio = value / 255
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

const contrastRatio = (foreground: string, background: string) => {
  const first = relativeLuminance(foreground)
  const second = relativeLuminance(background)
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)
  return (lighter + 0.05) / (darker + 0.05)
}

const ensureContrast = (
  color: string,
  background: string,
  minimumRatio: number,
  direction: "lighten" | "darken",
) => {
  if (contrastRatio(color, background) >= minimumRatio) {
    return color
  }

  const hsl = rgbToHsl(hexToRgb(color))
  for (let step = 1; step <= 32; step += 1) {
    const next = hslToHex({
      ...hsl,
      l: direction === "lighten"
        ? clamp(hsl.l + step * 0.025)
        : clamp(hsl.l - step * 0.025),
    })
    if (contrastRatio(next, background) >= minimumRatio) {
      return next
    }
  }

  return direction === "lighten" ? WHITE : BLACK
}

const getReadableTextColor = (background: string) => {
  return contrastRatio(WHITE, background) >= contrastRatio(BLACK, background) ? WHITE : BLACK
}

const formatRgbAlpha = (color: string, alpha: number) => {
  const { r, g, b } = hexToRgb(color)
  return `rgb(${r} ${g} ${b} / ${Math.round(clamp(alpha) * 100)}%)`
}

const rotateHue = (color: string, degrees: number, mode: ThemeColorMode) => {
  const hsl = rgbToHsl(hexToRgb(color))
  const saturation = mode === "dark"
    ? clamp(hsl.s * 0.7, 0.46, 0.72)
    : clamp(hsl.s * 0.78, 0.5, 0.76)
  const lightness = mode === "dark"
    ? clamp(Math.max(hsl.l, 0.58), 0.42, 0.76)
    : clamp(hsl.l, 0.36, 0.58)
  return hslToHex({
    h: hsl.h + degrees,
    s: saturation,
    l: lightness,
  })
}

const getPrimaryForMode = (seedColor: string, mode: ThemeColorMode) => {
  if (mode === "dark") {
    return ensureContrast(seedColor, DARK_SURFACE, 3, "lighten")
  }
  return ensureContrast(seedColor, LIGHT_SURFACE, 3, "darken")
}

const getLightActiveBlockBg = (primary: string) => {
  return mixColor(primary, WHITE, 0.9)
}

const buildTone = (
  seedColor: string,
  saturation: number,
  lightness: number,
  hueOffset = 0,
) => {
  const hsl = rgbToHsl(hexToRgb(seedColor))
  return hslToHex({
    h: hsl.h + hueOffset,
    s: saturation,
    l: lightness,
  })
}

const getDarkSurfaceHueOffset = (seedColor: string) => {
  const { h } = rgbToHsl(hexToRgb(seedColor))
  return h >= 130 && h <= 190 ? 38 : 0
}

const buildLightSemanticColors = (
  seedColor: string,
  primary: string,
): ThemeSemanticColors => {
  const { h, s } = rgbToHsl(hexToRgb(seedColor))
  const tintSaturation = clamp(Math.max(s * 0.05, 0.01), 0.01, 0.05)
  const tone = (lightness: number) => hslToHex({ h, s: tintSaturation, l: lightness })
  const pageBg = tone(0.945)
  const surfaceBg = tone(0.97)
  const elevatedBg = tone(0.99)
  const activeBlockBg = getLightActiveBlockBg(primary)
  const activeBlockText = ensureContrast(primary, activeBlockBg, 4.5, "darken")
  return {
    pageBg,
    surfaceBg,
    elevatedBg,
    textPrimary: "#1F2329",
    textRegular: "#4E5969",
    textSecondary: "#86909C",
    textPlaceholder: "#C9CDD4",
    textDisabled: "#566273",
    textInk: "#000000E6",
    fillSubtle: tone(0.955),
    fillMuted: tone(0.925),
    fillDisabled: tone(0.9),
    borderSubtle: tone(0.9),
    border: tone(0.855),
    borderStrong: tone(0.78),
    chartAxis: "#86909C",
    chartGrid: tone(0.89),
    chartTooltipBg: elevatedBg,
    chartTooltipBorder: tone(0.855),
    tableHeaderBg: tone(0.955),
    tableRowHoverBg: tone(0.955),
    tagMutedBg: tone(0.9),
    tagNeutralBg: tone(0.87),
    sidebarBg: surfaceBg,
    sidebarText: "#1F2329",
    sidebarActiveBg: tone(0.93),
    businessContainerBg: tone(0.925),
    categoryNameColor: "#000000CC",
    breadcrumbColor: mixColor(primary, BLACK, 0.35),
    categoryListBg: elevatedBg,
    categoryListColor: "#5F6B7A",
    categoryListActiveBg: activeBlockBg,
    categoryListActiveColor: activeBlockText,
    categoryListActiveHoverBg: mixColor(activeBlockBg, WHITE, 0.45),
    businessButtonBg: `linear-gradient(180deg, ${WHITE} 0%, ${tone(0.92)} 100%)`,
    businessButtonText: "#303133",
    businessButtonBorder: "1px solid rgb(0 0 0 / 10%)",
    businessButtonHoverBg: tone(0.9),
    businessButtonHoverShadow: "1px 1px 4px 0 rgb(255 255 255 / 50%)",
    businessButtonActiveText: "#606266",
    businessButtonActiveBg: tone(0.94),
    tableEmptyText: "#303133",
    loadingMaskBg: formatRgbAlpha(surfaceBg, 0.86),
    pageLoadingMaskBg: formatRgbAlpha(pageBg, 0.88),
    pageLoadingSpinnerColor: "#4E5969",
  }
}

const buildBlackGoldDarkSemanticColors = (): ThemeSemanticColors => {
  const pageBg = "#24221F"
  const surfaceBg = "#2B2925"
  const elevatedBg = "#3B3939"
  const businessContainerBg = "#3D3A3A"
  const categoryListBg = "#7F7D7A"
  const categoryListHoverBg = "#6D6A68"

  return {
    pageBg,
    surfaceBg,
    elevatedBg,
    textPrimary: "#FFFFFF",
    textRegular: "#F0ECE4",
    textSecondary: "#D4CDC0",
    textPlaceholder: "#9F978A",
    textDisabled: "#746E64",
    textInk: "#FFFFFF",
    fillSubtle: "#3A3734",
    fillMuted: "#484541",
    fillDisabled: "#5A554D",
    borderSubtle: "#4B4742",
    border: "#68635C",
    borderStrong: "#8A8276",
    chartAxis: "#CFC7B7",
    chartGrid: "#4B4742",
    chartTooltipBg: "#34312D",
    chartTooltipBorder: "#6F675B",
    tableHeaderBg: "#3A3734",
    tableRowHoverBg: "#34312D",
    tagMutedBg: "#514C43",
    tagNeutralBg: "#625C52",
    sidebarBg: pageBg,
    sidebarText: "#F6F0E5",
    sidebarActiveBg: "#3D3A3A",
    businessContainerBg,
    categoryNameColor: "#FFFFFF",
    breadcrumbColor: "#D8CFBF",
    categoryListBg,
    categoryListColor: "#FFFFFF",
    categoryListActiveBg: BLACK_GOLD_THEME_COLOR,
    categoryListActiveColor: "#24211C",
    categoryListActiveHoverBg: categoryListHoverBg,
    businessButtonBg: categoryListBg,
    businessButtonText: "#FFFFFF",
    businessButtonBorder: "1px solid rgb(255 255 255 / 8%)",
    businessButtonHoverBg: categoryListHoverBg,
    businessButtonHoverShadow: "none",
    businessButtonActiveText: "#24211C",
    businessButtonActiveBg: BLACK_GOLD_THEME_COLOR,
    tableEmptyText: "#D8CFBF",
    loadingMaskBg: formatRgbAlpha(surfaceBg, 0.86),
    pageLoadingMaskBg: formatRgbAlpha(businessContainerBg, 0.88),
    pageLoadingSpinnerColor: "#F0ECE4",
  }
}

const buildDarkSemanticColors = (
  seedColor: string,
): ThemeSemanticColors => {
  if (seedColor === BLACK_GOLD_THEME_COLOR) {
    return buildBlackGoldDarkSemanticColors()
  }

  const { s } = rgbToHsl(hexToRgb(seedColor))
  const hueOffset = getDarkSurfaceHueOffset(seedColor)
  const surfaceSaturation = clamp(Math.max(s * 0.32, 0.12), 0.1, 0.34)
  const tone = (lightness: number) => buildTone(seedColor, surfaceSaturation, lightness, hueOffset)
  const pageBg = tone(0.085)
  const surfaceBg = tone(0.13)
  const elevatedBg = tone(0.17)

  return {
    pageBg,
    surfaceBg,
    elevatedBg,
    textPrimary: "#F5F7FA",
    textRegular: "#DDE5EF",
    textSecondary: "#B4C0CE",
    textPlaceholder: "#8491A3",
    textDisabled: "#667386",
    textInk: "#F5F7FA",
    fillSubtle: tone(0.2),
    fillMuted: tone(0.245),
    fillDisabled: tone(0.29),
    borderSubtle: tone(0.29),
    border: tone(0.36),
    borderStrong: tone(0.48),
    chartAxis: "#A3A6AD",
    chartGrid: tone(0.28),
    chartTooltipBg: tone(0.18),
    chartTooltipBorder: tone(0.33),
    tableHeaderBg: tone(0.2),
    tableRowHoverBg: tone(0.2),
    tagMutedBg: tone(0.24),
    tagNeutralBg: tone(0.33),
    sidebarBg: tone(0.14),
    sidebarText: "#E5EAF3",
    sidebarActiveBg: tone(0.24),
    businessContainerBg: tone(0.1),
    categoryNameColor: "#E5EAF3",
    breadcrumbColor: "#CFD3DC",
    categoryListBg: tone(0.18),
    categoryListColor: "#CFD3DC",
    categoryListActiveBg: tone(0.24),
    categoryListActiveColor: "#F5F7FA",
    categoryListActiveHoverBg: tone(0.2),
    businessButtonBg: tone(0.24),
    businessButtonText: "#E5EAF3",
    businessButtonBorder: `1px solid ${tone(0.33)}`,
    businessButtonHoverBg: tone(0.33),
    businessButtonHoverShadow: "none",
    businessButtonActiveText: "#A3A6AD",
    businessButtonActiveBg: tone(0.18),
    tableEmptyText: "#E5EAF3",
    loadingMaskBg: formatRgbAlpha(surfaceBg, 0.86),
    pageLoadingMaskBg: formatRgbAlpha(pageBg, 0.88),
    pageLoadingSpinnerColor: "#DDE5EF",
  }
}

export const buildThemeColorScheme = (
  color: string | null | undefined,
  mode: ThemeColorMode,
): ThemeColorScheme => {
  const seedColor = normalizeHexColor(color) || DEFAULT_THEME_COLOR
  const primary = getPrimaryForMode(seedColor, mode)
  const onPrimary = getReadableTextColor(primary)
  const { r, g, b } = hexToRgb(primary)

  if (mode === "dark") {
    const primaryLight3 = mixColor(primary, DARK_MIX_BG, 0.3)
    const primaryLight5 = mixColor(primary, DARK_MIX_BG, 0.5)
    const primaryLight7 = mixColor(primary, DARK_MIX_BG, 0.7)
    const primaryLight8 = mixColor(primary, DARK_MIX_BG, 0.8)
    const primaryLight9 = mixColor(primary, DARK_MIX_BG, 0.9)
    const semanticColors = buildDarkSemanticColors(seedColor)
    return {
      seedColor,
      mode,
      primary,
      hover: mixColor(primary, WHITE, 0.18),
      active: mixColor(primary, BLACK, 0.18),
      onPrimary,
      primaryRgb: `${r} ${g} ${b}`,
      primarySubtle: primaryLight9,
      primary50: primaryLight9,
      primary100: primaryLight8,
      primary200: primaryLight7,
      primary300: primaryLight5,
      primary400: primaryLight3,
      primaryLight3,
      primaryLight5,
      primaryLight7,
      primaryLight8,
      primaryLight9,
      primaryDark2: mixColor(primary, WHITE, 0.2),
      ...semanticColors,
      chartColors: [primary, ...CHART_HUE_OFFSETS.map(offset => rotateHue(primary, offset, mode))],
    }
  }

  const primaryLight3 = mixColor(primary, WHITE, 0.3)
  const primaryLight5 = mixColor(primary, WHITE, 0.5)
  const primaryLight7 = mixColor(primary, WHITE, 0.7)
  const primaryLight8 = mixColor(primary, WHITE, 0.8)
  const primaryLight9 = mixColor(primary, WHITE, 0.9)
  const semanticColors = buildLightSemanticColors(seedColor, primary)

  return {
    seedColor,
    mode,
    primary,
    hover: mixColor(primary, WHITE, 0.15),
    active: mixColor(primary, BLACK, 0.2),
    onPrimary,
    primaryRgb: `${r} ${g} ${b}`,
    primarySubtle: primaryLight9,
    primary50: primaryLight9,
    primary100: primaryLight8,
    primary200: primaryLight7,
    primary300: primaryLight5,
    primary400: primaryLight3,
    primaryLight3,
    primaryLight5,
    primaryLight7,
    primaryLight8,
    primaryLight9,
    primaryDark2: mixColor(primary, BLACK, 0.2),
    ...semanticColors,
    chartColors: [primary, ...CHART_HUE_OFFSETS.map(offset => rotateHue(primary, offset, mode))],
  }
}

const getPrimaryTextButtonTextColors = (scheme: ThemeColorScheme) => {
  const background = scheme.mode === "dark" ? scheme.elevatedBg : scheme.surfaceBg
  const direction = scheme.mode === "dark" ? "lighten" : "darken"
  const getTextColor = (color: string) => ensureContrast(
    scheme.mode === "dark" ? mixColor(color, WHITE, 0.45) : color,
    background,
    4.5,
    direction,
  )

  return {
    text: getTextColor(scheme.primary),
    hoverText: getTextColor(scheme.hover),
    activeText: getTextColor(scheme.active),
  }
}

export const applyThemeColorScheme = (
  color: string | null | undefined,
  mode: ThemeColorMode,
  root: HTMLElement | null = typeof document === "undefined" ? null : document.documentElement,
) => {
  const scheme = buildThemeColorScheme(color, mode)
  if (!root) {
    return scheme
  }

  const primaryTextButtonTextColors = getPrimaryTextButtonTextColors(scheme)
  const styleVars: Record<string, string> = {
    "--tea-color-primary": scheme.primary,
    "--tea-color-primary-hover": scheme.hover,
    "--tea-color-primary-active": scheme.active,
    "--tea-color-primary-rgb": scheme.primaryRgb,
    "--tea-color-primary-subtle": scheme.fillSubtle,
    "--tea-color-primary-50": scheme.primary50,
    "--tea-color-primary-100": scheme.primary100,
    "--tea-color-primary-200": scheme.primary200,
    "--tea-color-primary-300": scheme.primary300,
    "--tea-color-primary-400": scheme.primary400,
    "--tea-color-primary-light-3": scheme.primaryLight3,
    "--tea-color-primary-light-5": scheme.primaryLight5,
    "--tea-color-primary-light-7": scheme.primaryLight7,
    "--tea-color-primary-light-8": scheme.primaryLight8,
    "--tea-color-primary-light-9": scheme.primaryLight9,
    "--tea-color-primary-dark-2": scheme.primaryDark2,
    "--tea-color-on-primary": scheme.onPrimary,
    "--tea-color-text-primary": scheme.textPrimary,
    "--tea-color-text-regular": scheme.textRegular,
    "--tea-color-text-secondary": scheme.textSecondary,
    "--tea-color-text-placeholder": scheme.textPlaceholder,
    "--tea-color-text-disabled": scheme.textDisabled,
    "--tea-color-text-ink": scheme.textInk,
    "--tea-color-bg-page": scheme.pageBg,
    "--tea-color-bg-surface": scheme.surfaceBg,
    "--tea-color-bg-elevated": scheme.elevatedBg,
    "--tea-color-fill-subtle": scheme.fillSubtle,
    "--tea-color-fill-muted": scheme.fillMuted,
    "--tea-color-fill-disabled": scheme.fillDisabled,
    "--tea-color-border-subtle": scheme.borderSubtle,
    "--tea-color-border": scheme.border,
    "--tea-color-border-strong": scheme.borderStrong,
    "--tea-color-focus-ring": `rgb(${scheme.primaryRgb} / ${mode === "dark" ? "16%" : "12%"})`,
    "--tea-color-chart-axis": scheme.chartAxis,
    "--tea-color-chart-grid": scheme.chartGrid,
    "--tea-color-chart-tooltip-bg": scheme.chartTooltipBg,
    "--tea-color-chart-tooltip-border": scheme.chartTooltipBorder,
    "--tea-color-chart-area-primary-start": `rgb(${scheme.primaryRgb} / 22%)`,
    "--tea-color-chart-area-primary-end": `rgb(${scheme.primaryRgb} / 3%)`,
    "--tea-component-table-header-bg-color": scheme.tableHeaderBg,
    "--tea-component-table-row-hover-bg-color": scheme.tableRowHoverBg,
    "--tea-component-tag-primary-bg-color": scheme.primarySubtle,
    "--tea-component-tag-muted-bg-color": scheme.tagMutedBg,
    "--tea-component-tag-neutral-bg-color": scheme.tagNeutralBg,
    "--tea-component-button-primary-text-color": scheme.onPrimary,
    "--tea-component-button-primary-bg-color": scheme.primary,
    "--tea-component-button-primary-hover-bg-color": scheme.hover,
    "--tea-component-button-primary-active-bg-color": scheme.active,
    "--tea-component-button-primary-border-color": scheme.primary,
    "--tea-component-button-primary-hover-border-color": scheme.hover,
    "--tea-component-button-primary-active-border-color": scheme.active,
    "--tea-component-button-primary-text-button-text-color": primaryTextButtonTextColors.text,
    "--tea-component-button-primary-text-button-hover-text-color": primaryTextButtonTextColors.hoverText,
    "--tea-component-button-primary-text-button-active-text-color": primaryTextButtonTextColors.activeText,
    "--tea-component-form-control-focus-border-color": `rgb(${scheme.primaryRgb} / ${mode === "dark" ? "46%" : "30%"})`,
    "--tea-component-member-action-text-color": scheme.onPrimary,
    "--tea-component-member-action-bg-color": scheme.primary,
    "--tea-component-member-action-hover-bg-color": scheme.hover,
    "--tea-component-member-action-active-bg-color": scheme.active,
    "--tea-component-member-action-border-color": scheme.primary,
    "--tea-component-member-action-hover-border-color": scheme.hover,
    "--tea-component-member-action-active-border-color": scheme.active,
    "--tea-component-badge-bg-color": scheme.primary,
    "--tea-business-sidebar-bg-color": scheme.sidebarBg,
    "--tea-business-sidebar-text-color": scheme.sidebarText,
    "--tea-business-sidebar-active-bg-color": scheme.sidebarActiveBg,
    "--tea-business-sidebar-active-text-color": scheme.primary,
    "--tea-business-sidebar-tip-line-color": scheme.primary,
    "--tea-business-container-bg-color": scheme.businessContainerBg,
    "--tea-business-category-name-color": scheme.categoryNameColor,
    "--tea-business-breadcrumb-color": scheme.breadcrumbColor,
    "--tea-business-category-list-bg-color": scheme.categoryListBg,
    "--tea-business-category-list-color": scheme.categoryListColor,
    "--tea-business-category-list-active-bg-color": scheme.categoryListActiveBg,
    "--tea-business-category-list-active-color": scheme.categoryListActiveColor,
    "--tea-business-category-list-active-hover-bg-color": scheme.categoryListActiveHoverBg,
    "--tea-business-button-bg-color": scheme.businessButtonBg,
    "--tea-business-button-text-color": scheme.businessButtonText,
    "--tea-business-button-border": scheme.businessButtonBorder,
    "--tea-business-button-hover-bg-color": scheme.businessButtonHoverBg,
    "--tea-business-button-hover-shadow": scheme.businessButtonHoverShadow,
    "--tea-business-button-active-text-color": scheme.businessButtonActiveText,
    "--tea-business-button-active-bg-color": scheme.businessButtonActiveBg,
    "--tea-business-table-empty-text-color": scheme.tableEmptyText,
    "--tea-component-loading-mask-bg-color": scheme.loadingMaskBg,
    "--tea-component-page-loading-mask-bg-color": scheme.pageLoadingMaskBg,
    "--tea-component-page-loading-spinner-color": scheme.pageLoadingSpinnerColor,
  }

  scheme.chartColors.forEach((chartColor, index) => {
    styleVars[`--tea-color-chart-${index + 1}`] = chartColor
  })

  for (const [key, value] of Object.entries(styleVars)) {
    root.style.setProperty(key, value)
  }

  return scheme
}
